/**
 * PDF da minuta do Manifesto com o mesmo timbrado das Propostas
 * (cabeçalho, rodapé, assinatura e numeração — Configuração da empresa).
 */
import jsPDF from 'jspdf';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { entities } from '@/api/apiClient';
import { toast } from 'sonner';
import {
  SIDE_MARGIN,
  CABECALHO_RESERVE_EXTRA_MM,
  loadImageDataUrlForPdf,
  measureCabecalhoBlockHeightMm,
  measureRodapeBlockHeightMm,
  addCabecalhoEmTodasAsPaginas,
  addRodapeEmTodasAsPaginas,
  addPageNumbers,
  signatureBlockPlaceAssinaturaImage,
  setPdfLayoutBottomReserveMm,
  setPdfLayoutTopReserveMm,
  ensureY,
  estimateSignatureBlockHeightMm,
} from '@/lib/propostaTextoPdfExport';
import { htmlToPlainTextForPdf } from '@/lib/htmlPlainText';
import { captureRodapeTextoHtmlForPdf } from '@/lib/rodapeTextoPdfRaster';
import {
  ASSINATURA_CORES,
  iniciaisNomeCompleto,
  formatCnpjParaExibicao,
} from '@/utils/empresaAssinatura';

const BODY_PT = 10;
const SMALL_PT = 9;
const PAGE_FOOTER_RESERVE_MM = 12;
const BOTTOM_SAFE = 14;

function disp(v, fallback = 'Não informado') {
  if (v === null || v === undefined || v === '') return fallback;
  return String(v);
}

function configRazao(config) {
  return (config?.razao_social || config?.razaoSocial || '').trim();
}

function configCnpj(config) {
  return String(config?.cnpj || config?.CNPJ || '').trim();
}

function estimateParagraphBlockHeightMm(doc, text, width, lineHeight = 4.8) {
  if (!text) return 0;
  doc.setFontSize(BODY_PT);
  doc.setFont('helvetica', 'normal');
  const lines = doc.splitTextToSize(String(text), width);
  return lines.length * lineHeight + 1;
}

function paragraph(doc, text, x, y, width, lineHeight = 4.8) {
  if (!text) return y;
  doc.setFontSize(BODY_PT);
  doc.setFont('helvetica', 'normal');
  const lines = doc.splitTextToSize(String(text), width);
  for (const line of lines) {
    y = ensureY(doc, y, lineHeight);
    doc.text(line, x, y);
    y += lineHeight;
  }
  return y + 1;
}

/**
 * Monta o jsPDF da minuta (conteúdo + fecho + timbrado aplicado ao final).
 */
export async function buildManifestoMinutaPdfDoc({
  tipoLabel: _tipoLabel,
  etapaLabel: _etapaLabel = 'Protocolo',
  editalLabel: _editalLabel,
  minutaText,
  config,
}) {
  const [cabImg, rodImg, assImg] = await Promise.all([
    loadImageDataUrlForPdf(config?.cabecalho_url ?? config?.cabecalhoUrl),
    loadImageDataUrlForPdf(config?.rodape_url ?? config?.rodapeUrl),
    loadImageDataUrlForPdf(config?.assinatura_url ?? config?.assinaturaUrl),
  ]);

  const doc = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const contentW = pageW - 2 * SIDE_MARGIN;
  const rodapeTexto = config?.rodape_texto;
  let rodapeRaster = null;
  if (!rodImg && rodapeTexto && String(rodapeTexto).trim()) {
    rodapeRaster = await captureRodapeTextoHtmlForPdf({
      html: rodapeTexto,
      contentWidthMm: contentW,
    });
  }
  const rodapePlain = rodapeTexto ? htmlToPlainTextForPdf(String(rodapeTexto)).trim() : '';
  const hasRodape = Boolean(rodImg || rodapeRaster?.dataUrl || rodapePlain);
  const bottomReserve = hasRodape
    ? PAGE_FOOTER_RESERVE_MM + measureRodapeBlockHeightMm(doc, rodImg, rodapeTexto, contentW, rodapeRaster)
    : BOTTOM_SAFE;
  setPdfLayoutBottomReserveMm(bottomReserve);

  const cabTexto = config?.cabecalho_texto;
  const cabPlain = cabTexto ? htmlToPlainTextForPdf(String(cabTexto)).trim() : '';
  const hasCabecalho = Boolean(cabImg || cabPlain);
  const topReserve = hasCabecalho
    ? measureCabecalhoBlockHeightMm(doc, cabImg, cabTexto, contentW) + CABECALHO_RESERVE_EXTRA_MM
    : 0;
  setPdfLayoutTopReserveMm(topReserve);

  const contentStartYMm = SIDE_MARGIN + topReserve;
  let y = contentStartYMm;

  try {
    const body = String(minutaText || '').trim() || '(sem conteúdo na minuta)';
    y = paragraph(doc, body, SIDE_MARGIN, y, contentW);

    const dataExtenso = format(new Date(), "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
    const textoLocalData = `${disp(config?.municipio)} / ${disp(config?.uf)}, ${dataExtenso}`;

    const alturaFecho =
      estimateParagraphBlockHeightMm(doc, textoLocalData, contentW) + estimateSignatureBlockHeightMm(Boolean(assImg));
    y = ensureY(doc, y, alturaFecho + 8);

    y = paragraph(doc, textoLocalData, SIDE_MARGIN, y, contentW);

    const { marcaRgb, avatarFillRgb, avatarStrokeRgb } = ASSINATURA_CORES;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(SMALL_PT);
    doc.setTextColor(51, 65, 85);
    doc.text('Atenciosamente,', SIDE_MARGIN, y);
    doc.setTextColor(0, 0, 0);
    y += 5;

    const { y: yAfterSig, sigLineW } = signatureBlockPlaceAssinaturaImage(doc, assImg, y, contentW);
    y = yAfterSig;

    doc.setDrawColor(30, 41, 59);
    doc.setLineWidth(0.3);
    doc.line(SIDE_MARGIN, y, SIDE_MARGIN + sigLineW, y);
    y += 4;

    const badgeR = 7;
    const badgeCx = SIDE_MARGIN + badgeR;
    const badgeCy = y + badgeR;
    doc.setFillColor(...avatarFillRgb);
    doc.setDrawColor(...avatarStrokeRgb);
    doc.setLineWidth(0.15);
    doc.circle(badgeCx, badgeCy, badgeR, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...marcaRgb);
    doc.text(iniciaisNomeCompleto(config?.socio_nome), badgeCx, badgeCy + 1.2, {
      align: 'center',
    });

    const textLeft = SIDE_MARGIN + badgeR * 2 + 4;
    doc.setTextColor(...marcaRgb);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(disp(config?.socio_nome), textLeft, y + 4);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(SMALL_PT);
    doc.setTextColor(71, 85, 105);
    doc.text(disp(config?.socio_cargo), textLeft, y + 9);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(SMALL_PT);
    doc.setTextColor(...marcaRgb);
    doc.text(disp(configRazao(config)), textLeft, y + 14);
    const cnpjFmt = formatCnpjParaExibicao(configCnpj(config));
    doc.text(`CNPJ ${cnpjFmt ?? disp(configCnpj(config))}`, textLeft, y + 19);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    y += 27;

    addCabecalhoEmTodasAsPaginas(doc, cabImg, cabTexto, contentW);
    addRodapeEmTodasAsPaginas(doc, rodImg, rodapeTexto, contentW, rodapeRaster);
    addPageNumbers(doc);

    return doc;
  } finally {
    setPdfLayoutBottomReserveMm(null);
    setPdfLayoutTopReserveMm(0);
  }
}

async function resolveConfig(explicitConfig) {
  if (explicitConfig) return explicitConfig;
  try {
    const list = await entities.ConfiguracaoEmpresa.list();
    return Array.isArray(list) && list[0] ? list[0] : null;
  } catch {
    return null;
  }
}

function buildManifestoMinutaPdfSimple({ tipoLabel: _tipoLabel, etapaLabel: _etapaLabel = 'Protocolo', editalLabel: _editalLabel, minutaText }) {
  const doc = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });
  const margin = 14;
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const contentW = pageW - margin * 2;
  let y = margin;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  const raw = String(minutaText || '').trim() || '(sem conteúdo)';
  const paragraphs = raw.split('\n');
  const lineH = 5;
  for (const p of paragraphs) {
    if (!p.trim()) {
      y += lineH * 0.5;
      continue;
    }
    const lines = doc.splitTextToSize(p, contentW);
    for (const line of lines) {
      if (y > pageH - margin - 10) {
        doc.addPage();
        y = margin;
      }
      doc.text(line, margin, y);
      y += lineH;
    }
  }
  addPageNumbers(doc);
  return doc;
}

/**
 * Gera PDF no padrão da proposta (timbrado) e abre a impressão.
 * @param {object} p
 * @param {object} [p.config] — Configuração da empresa; se omitido, busca via API.
 */
export async function printManifestoMinutaPdf({ tipoLabel, etapaLabel = 'Protocolo', editalLabel, minutaText, config: configIn }) {
  const config = await resolveConfig(configIn);
  if (!config) {
    toast.warning(
      'Sem dados em Configuração da Empresa: impressão sem timbrado completo. Cadastre cabeçalho, rodapé e assinatura para igualar às Propostas.'
    );
  }
  const doc = config
    ? await buildManifestoMinutaPdfDoc({
        tipoLabel,
        etapaLabel,
        editalLabel,
        minutaText,
        config,
      })
    : buildManifestoMinutaPdfSimple({ tipoLabel, etapaLabel, editalLabel, minutaText });

  const blob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(blob);
  const printWindow = window.open(pdfUrl);
  if (printWindow) {
    printWindow.onload = () => printWindow.print();
  }
}
